var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { EColumnKind } from "../types";
/** Shown for a figure that is undefined, such as a ratio over zero. */
export var UNDEFINED_VALUE = "—";
var isNumber = function (value) {
    return typeof value === "number" && Number.isFinite(value);
};
export function formatInteger(value, locale) {
    return isNumber(value) ? new Intl.NumberFormat(locale).format(value) : UNDEFINED_VALUE;
}
/**
 * A fraction as a percentage, rounded half away from zero, with
 * `sequent_core::monitoring::compute::percent_label`'s rule: never all short of
 * all, nor none above none. 9,996 of 10,000 Posts closed is 99.9%, not 100.0%.
 */
function percent(value, locale, minimumDigits, maximumDigits) {
    var format = new Intl.NumberFormat(locale, {
        style: "percent",
        minimumFractionDigits: minimumDigits,
        maximumFractionDigits: maximumDigits,
    }).format;
    var text = format(value);
    // The smallest step the text shows, as a fraction.
    var step = Math.pow(10, -(maximumDigits + 2));
    if (value < 1 && text === format(1))
        return format(1 - step);
    if (value > 0 && text === format(0))
        return format(step);
    return text;
}
/** A fraction as a percentage, `53.2%`, for KPIs. */
export function formatRatio(value, locale, digits) {
    if (digits === void 0) { digits = 1; }
    return isNumber(value) ? percent(value, locale, digits, digits) : UNDEFINED_VALUE;
}
/** Digits a table shows of a ratio: enough to tell 99.96% from 100%. */
var TABLE_RATIO_DIGITS = 4;
/** `647K`, as on charts. */
export function formatCompact(value, locale) {
    return isNumber(value)
        ? new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }).format(value)
        : UNDEFINED_VALUE;
}
/** A result cell, exact enough to check a chart against. */
export function formatCell(value, kind, locale) {
    if (value === null || value === undefined)
        return UNDEFINED_VALUE;
    switch (kind) {
        case EColumnKind.INTEGER:
            return formatInteger(value, locale);
        case EColumnKind.NUMBER:
            return isNumber(value) ? percent(value, locale, 0, TABLE_RATIO_DIGITS) : UNDEFINED_VALUE;
        default:
            return typeof value === "string" ? value : JSON.stringify(value);
    }
}
function dateTimeFormat(locale, timeZone, options) {
    try {
        return new Intl.DateTimeFormat(locale, __assign(__assign({}, options), { timeZone: timeZone }));
    }
    catch (_a) {
        return new Intl.DateTimeFormat(locale, options);
    }
}
/** A moment in the event's time zone; the viewer's zone if that one is unknown. */
export function formatDateTime(iso, timeZone, locale, options) {
    if (options === void 0) { options = { dateStyle: "medium", timeStyle: "short" }; }
    var date = iso ? new Date(iso) : null;
    if (!date || Number.isNaN(date.getTime()))
        return UNDEFINED_VALUE;
    return dateTimeFormat(locale, timeZone, options).format(date);
}
